import type { Assignee } from "@/domain/model/Assignee";
import type { AssigneesService } from "@/domain/port/AssigneesService";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type { AssigneeDto } from "./dto/AssigneeDto";
import { assigneeFromDto } from "./mappers/AssigneeMapper";

const BATCH_LIMIT = 100;

export class AssigneesServiceImpl implements AssigneesService {
  constructor(private readonly http: HttpClient) {}

  async list(q: PageQuery): Promise<{ items: Assignee[]; total: number }> {
    const params = new URLSearchParams({
      page: String(q.page),
      pageSize: String(q.pageSize),
    });

    const { items, total } = await this.http.request<{
      items: AssigneeDto[];
      total: number;
    }>(`/api/assignees?${params.toString()}`);

    return { items: items.map(assigneeFromDto), total };
  }

  async getManyByIds(ids: string[]): Promise<Assignee[]> {
    if (ids.length === 0) return [];

    // chunk to respect server max
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + BATCH_LIMIT));
    }

    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const qs = new URLSearchParams({ ids: chunk.join(",") }); // CSV as per your route
        const dtos = await this.http.request<AssigneeDto[]>(
          `/api/assignees/_batch?${qs.toString()}`,
        );
        return dtos.map(assigneeFromDto);
      }),
    );

    return results.flat();
  }
}
